// Appels d'API de l'HISTORIQUE DES MESURES (tableau de bord du projet).
//
// Les relevés viennent des synchronisations de connecteurs et des saisies
// manuelles. La clé de cache commence par `['metrics', projectId]` : toute
// écriture qui rafraîchit les indicateurs du projet (une synchro, un import)
// rafraîchit donc aussi les courbes, sans rien ajouter ailleurs.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';

export interface MeasurePoint {
  at: string;
  value: number;
}

export interface MeasureHistory {
  key: string;
  name: string;
  unit: string | null;
  period: string | null;
  source: string;
  sourceLabel: string;
  /** Saisie à la main (et non relevée par un connecteur). */
  manual: boolean;
  value: number;
  lastAt: string;
  /** Le dernier relevé : seul un relevé manuel peut être retiré. */
  lastId: string | null;
  count: number;
  change: { delta: number; pct: number | null; label: string } | null;
  points: MeasurePoint[];
  /** Ordonnées de la mini-courbe, entre 0 (plus bas) et 1 (plus haut), calculées par le serveur. */
  spark: number[];
}

export interface MeasuresResponse {
  measures: MeasureHistory[];
  canWrite: boolean;
}

export interface ManualMeasureInput {
  name: string;
  value: string;
  unit?: string;
  period?: string;
  /** AAAA-MM-JJ ; vide = aujourd'hui. */
  recordedOn?: string;
}

export interface HeartLetter {
  letter: string;
  label: string;
  /** Ce qu'il faut brancher ou saisir, quand la lettre est vide. */
  hint: string;
  items: {
    key: string;
    name: string;
    value: number;
    unit: string | null;
    change: { label: string } | null;
    source: string;
    /** Affichée, mais écartée du score par le modèle du produit. */
    outOfScore: boolean;
  }[];
}

const historyKey = (projectId: string) => ['metrics', projectId, 'history'] as const;

export function useHeart(projectId: string) {
  return useQuery({
    queryKey: ['metrics', projectId, 'heart'] as const,
    queryFn: () => api.get<{ letters: HeartLetter[] }>(`/api/projects/${encodeURIComponent(projectId)}/heart`),
    enabled: Boolean(projectId),
    staleTime: 30 * 1000,
  });
}

export function useMeasureHistory(projectId: string) {
  return useQuery({
    queryKey: historyKey(projectId),
    queryFn: () => api.get<MeasuresResponse>(`/api/projects/${encodeURIComponent(projectId)}/measures`),
    enabled: Boolean(projectId),
    staleTime: 30 * 1000,
  });
}

export function useAddMeasure(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ManualMeasureInput) =>
      api.post<{ id: string; key: string }>(`/api/projects/${encodeURIComponent(projectId)}/measures`, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: historyKey(projectId) }),
  });
}

export type ScoreKind = 'nps' | 'csat';

/** Le score calculé par le serveur à partir des notes collées. */
export interface ScoreResult {
  kind: ScoreKind;
  value: number;
  n: number;
  /** NPS seulement : répartition, en %. */
  promoters?: number;
  passives?: number;
  detractors?: number;
  /** Notes hors échelle, et morceaux qui n'étaient pas des nombres. */
  outOfScale: number;
  ignored: number;
  /** Moins de 30 réponses. */
  fragile: boolean;
}

/** NPS / CSAT : `preview` calcule sans enregistrer ; sinon le score devient une mesure. */
export function useScoreMeasure(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { kind: ScoreKind; scores: string; recordedOn?: string; preview?: boolean }) =>
      api.post<{ result: ScoreResult; id?: string; key?: string }>(
        `/api/projects/${encodeURIComponent(projectId)}/measures/score`, input),
    onSuccess: (_data, input) => {
      if (!input.preview) void qc.invalidateQueries({ queryKey: historyKey(projectId) });
    },
  });
}

export function useRemoveMeasure(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<{ deleted: boolean }>(`/api/measures/${encodeURIComponent(id)}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: historyKey(projectId) }),
  });
}
